import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loading from '../Loading';
import { useApi } from '../../context/ApiProvider';
import { Typography, Box, Button } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  horizontalScrollbarStyles,
  horizontalContainerStyles,
  horizontalCardStyles,
} from '../../styles/commonStyles';

const HorizontalSection = ({
  dataFrom,
  title,
  cardComponent: CardComponent,
  dataKey = 'items',
  showGenreLink = true,
  showDetailedInfo = false,
  defaultOrderBy = 'latest_track_date',
  defaultOrderDirection = 'desc',
  cardStyles = horizontalCardStyles,
  extraCardProps = {},
  items, // Optional: if provided, use this data instead of fetching
  loading: externalLoading, // Optional: if provided, use this loading state
  seeMoreUrl, // URL for the "See More" button
  viewMoreUrl, // URL for the "View More" button (alternative to seeMoreUrl)
  fetchDelay, // Optional: delay in ms before fetching (0 or unspecified = fetch immediately)
}) => {
  const [loading, setLoading] = useState(false);
  const [internalItems, setInternalItems] = useState([]);
  const [pagination, setPagination] = useState({});
  const [paginationConfig, setPaginationConfig] = useState({
    order_by: defaultOrderBy,
    order_direction: defaultOrderDirection,
  });

  const api = useApi();
  const navigate = useNavigate();

  // Use external items if provided, otherwise use internal state
  const displayItems = items || internalItems;
  const displayLoading =
    externalLoading !== undefined ? externalLoading : loading;

  useEffect(() => {
    // Only fetch data if no external items are provided
    if (!items) {
      if (fetchDelay && fetchDelay > 0) {
        const timer = setTimeout(loadItems, fetchDelay);
        return () => clearTimeout(timer);
      }
      loadItems();
    }
  }, [paginationConfig, dataFrom, items, fetchDelay]);

  async function loadItems() {
    if (!dataFrom) return; // Don't fetch if no dataFrom provided

    setLoading(true);
    try {
      const response = await api.get(dataFrom, paginationConfig);
      if (response.ok) {
        const data = response.body.data ? response.body.data : response.body;
        setInternalItems(data);
        setPagination(response.body.pagination);
      } else {
        console.error(`Failed to load ${dataKey}:`, response);
        setInternalItems([]);
      }
    } catch (error) {
      console.error(`Error loading ${dataKey}:`, error);
      setInternalItems([]);
    } finally {
      setLoading(false);
    }
  }

  const handleSeeMore = () => {
    if (seeMoreUrl) {
      navigate(seeMoreUrl);
    }
  };

  const handleViewMore = () => {
    if (viewMoreUrl) {
      navigate(viewMoreUrl);
    }
  };

  return (
    <>
      {title && (
        <Typography variant="h5" color={'white'} sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      {displayLoading && (
        <Box textAlign="center">
          <Loading alterColor />
        </Box>
      )}
      <Box
        display={displayLoading ? 'none' : 'block'}
        sx={horizontalScrollbarStyles}
      >
        <Box sx={horizontalContainerStyles}>
          {displayItems.map((item, index) => (
            <Box key={`${index}-${item.id}`} sx={cardStyles}>
              <CardComponent
                item={item}
                showDetailedInfo={showDetailedInfo}
                {...extraCardProps}
              />
            </Box>
          ))}
          {seeMoreUrl && (
            <Box sx={cardStyles}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSeeMore}
                endIcon={<NavigateNext />}
                sx={{
                  height: '100%',
                  minHeight: '200px',
                  width: '100%',
                  border: '2px solid',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(222, 255, 123, 0.04)',
                  },
                  '&:active': {
                    backgroundColor: 'rgba(222, 255, 123, 0.08)',
                  },
                }}
              >
                See More
              </Button>
            </Box>
          )}
          {viewMoreUrl && (
            <Box sx={cardStyles}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleViewMore}
                sx={{
                  height: '100%',
                  minHeight: '200px',
                  width: '100%',
                  border: '2px solid',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(222, 255, 123, 0.04)',
                  },
                  '&:active': {
                    backgroundColor: 'rgba(222, 255, 123, 0.08)',
                  },
                }}
              >
                View More
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

HorizontalSection.propTypes = {
  dataFrom: PropTypes.string,
  title: PropTypes.string,
  cardComponent: PropTypes.elementType.isRequired,
  dataKey: PropTypes.string,
  showGenreLink: PropTypes.bool,
  showDetailedInfo: PropTypes.bool,
  defaultOrderBy: PropTypes.string,
  defaultOrderDirection: PropTypes.string,
  cardStyles: PropTypes.object,
  extraCardProps: PropTypes.object,
  items: PropTypes.array, // Optional: if provided, use this data instead of fetching
  loading: PropTypes.bool, // Optional: if provided, use this loading state
  seeMoreUrl: PropTypes.string, // URL for the "See More" button
  viewMoreUrl: PropTypes.string, // URL for the "View More" button (alternative to seeMoreUrl)
  fetchDelay: PropTypes.number,
};

export default HorizontalSection;
